import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { PartType } from './useGetSettings';
import { OrderPartFlagType } from './useGetOrderDetail';

export type VendorType = 'preferred' | 'avoid' | 'required' | 'super-preferred';

export type InvalidOptionReason =
    | 'oem-endorsement'
    | 'head-tail-lamps-with-logo'
    | 'oe-warranty'
    | 'welded-on-parts'
    | 'one-time-use-part'
    | 'incorrect-part-quoted'
    | 'vendor-misquoted-quantity'
    | 'invalid-pricing'
    | 'adhesive-templates'
    | 'srs-oem-only'
    | 'mileage-year-restricted'
    | 'adas-restricted'
    | 'insurer-guidelines'
    | 'supplier-disallowed'
    | 'used-cooling-component'
    | 'one-time-use-glass'
    | 'not-insurance-quality'
    | 'delivery-too-long'
    | 'invalid-part'
    | 'state-guidelines'
    | 'cannot-utilize-lkq'
    | 'cannot-utilize-recycled'
    | 'cannot-utilize-aftermarket'
    | 'cannot-utilize-recon'
    | 'cannot-utilize-prohibited'
    | 'cannot-utilize-assembly'
    | 'price-matched-to-assembly'
    | 'insufficient-stock';

export interface ValidOption {
    isValid: true;
}

export interface InvalidOption {
    isValid: false;
    reason: string;
    reasonKey: InvalidOptionReason;
}

export type OptionValidity = ValidOption | InvalidOption;

export interface PartOption {
    _id: string;
    supplierText: string;
    type: string;
    qty: number;
    listPrice: number;
    buyPrice: number;
    preAdjustmentBuyPrice?: number;
    selected?: boolean;
    delivery: number;
    deliveryString: string;
    source: 'ccc' | 'tesla' | 'partstrader' | 'opstrax' | 'ccc-sp' | 'inventory';
    originalSelected?: boolean;
    supplierNote: string;
    adjustedListPrice?: number;
    listPriceAdjusted: boolean;
    supplierPartNumber: string;
    insurerBest: boolean;
    bestPrice: boolean;
    vendorType: VendorType | null;
    partbayInsuranceBest?: boolean;
    supplierComment?: string;
    supplierId: string;
    selectable: boolean;
    validity?: OptionValidity;
    optionNote?: string;
}

export interface NoQuote {
    _id: string;
    supplierId: string;
    supplierText: string;
    noQuoteNumber: string;
}

export type TabOptions = 'all' | 'options' | 'noQuotes';
export interface PartOptionsResponse {
    title: string;
    partNumber: string;
    correctedPartNumber?: string;
    emsListPrice?: number;
    estimatePartType?: PartType | 'other';
    allowableListPrice?: number;
    flag: boolean;
    flagReason: string | null;
    flagType?: OrderPartFlagType | null;
    /** True when PartBay auto-excluded this line for incorrect-PN re-quote (ENG-2214). Missing = false. */
    excludedForIncorrectPartNumber?: boolean;
    /** Current ordering exclusion. Missing = false. */
    excludeOrdering?: boolean;
    selectedPartType: PartType | null;
    roNumber: string;
    insurance: string | null;
    estimator: 'ccc' | 'mitchell';
    preOrder?: boolean;
    oemEndorsement?: boolean;
    oemCertification: boolean;
    estimateNote?: string;
    mileage?: number;
    drpJob: boolean;
    adjustListPricePostPlacement: boolean;
    isFord: boolean;
    options: Array<PartOption>;
    allOptions: Array<PartOption>;
    noQuotes: Array<NoQuote>;
    defaultTab: TabOptions;
    partType: PartType;
    canConfirmSelections: boolean;
    canChangeSelections: boolean;
}

interface PartOptionsInput {
    orderPartId: string;
}

const getPartOptions = async (sessionId: string, orderPartId: string): Promise<PartOptionsResponse> => {
    const { data } = await axios.post(`${API_URL}/order/part`,
        { orderPartId } as PartOptionsInput,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useGetPartOptions = (orderPartId: string | null, enabled: boolean) => {
    const sessionContext = useContext(SessionContext);

    return useQuery({
        queryKey: ['/order/part', orderPartId],
        queryFn: () => getPartOptions(sessionContext?.sessionId ?? '', orderPartId!),
        enabled: !!sessionContext?.sessionId && !!orderPartId && enabled,
    });
};
